import { useState, useEffect } from "react";
import { LogoFichaje } from "@/components/fichaje/LogoFichaje";
import { DescargaFichaje } from "@/components/landing/DescargaFichaje";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";
import { PayMethodBadges } from "@/components/PayMethods";
import { LandingPlanPay } from "@/components/LandingPlanPay";
import { useMonedaVisitante } from "@/hooks/useMonedaVisitante";
import LoginDialog from "@/pages/Login";
import { LEGAL_LINKS } from "@/pages/Legal";
import { openCookieSettings } from "@/lib/consent";
import { HeroVideo } from "@/components/landing/HeroVideo";
import { SeccionNuvli } from "@/components/landing/SeccionNuvli";
import { useDatosSeo } from "@/publico/datos";
import { useCabecera } from "@/publico/cabecera";
import { metaDe } from "@/publico/metaRuta";
import { MapaWeb, useAnaliticaPublica } from "@/publico/PublicLayout";
import { CompartirWhatsApp } from "@/publico/Compartir";
import { LogIn, Loader2, CheckCircle2, ArrowRight, FileText, Wallet, Calculator, Star, ShieldCheck, Lock, CreditCard, Headphones, Receipt, PiggyBank, Users, HelpCircle, Mail, Send, Play, Cookie } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/c3b8cc1c-3015-4e97-8676-f68f1ec20976/images/a05a1541afb31b853f3db1e40614e780d4f61b5cce53ff3ca7213ba796373370.jpeg";
const IMG_BUILD = "https://static.prod-images.emergentagent.com/jobs/c3b8cc1c-3015-4e97-8676-f68f1ec20976/images/a05a1541afb31b853f3db1e40614e780d4f61b5cce53ff3ca7213ba796373370.jpeg";
const IMG_KITCHEN = "https://static.prod-images.emergentagent.com/jobs/c3b8cc1c-3015-4e97-8676-f68f1ec20976/images/6faa9eede7cfb96d028040130a152cfd3265af0f1f4b22241f4a2a6f26da9437.jpeg";
const IMG_TOOLS = "https://static.prod-images.emergentagent.com/jobs/c3b8cc1c-3015-4e97-8676-f68f1ec20976/images/669190bce69ee3596cabfd417ead231c691a4dd811173dba8a9bfe8b98c0fc9d.jpeg";
const DEMO_VIDEO_URL = process.env.REACT_APP_DEMO_VIDEO_URL || "";
const PLAN_ORDER = ["gratuito", "trimestral", "anual", "pro"];
const FEATURE_ICONS = [Receipt, FileText, Wallet, PiggyBank, Calculator, Users];
const SEAL_ICONS = [ShieldCheck, Lock, CreditCard, Headphones];
const num = (s) => parseFloat(String(s).replace(/[^\d,.]/g, "").replace(/\./g, "").replace(",", "."));

function ContactForm() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({ nombre: "", email: "", mensaje: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contacto", { ...form, idioma: i18n.resolvedLanguage || "es" });
      toast.success(t("home.contact.sent"));
      setForm({ nombre: "", email: "", mensaje: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("home.contact.error"));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-sky-100 shadow-lg shadow-sky-100/50 p-6 space-y-4" data-testid="contact-form">
      <div>
        <Label htmlFor="c-nombre">{t("home.contact.name")}</Label>
        <Input id="c-nombre" data-testid="contact-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required className="mt-1.5" placeholder={t("home.contact.namePh")} />
      </div>
      <div>
        <Label htmlFor="c-email">{t("home.contact.email")}</Label>
        <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" placeholder={t("home.contact.emailPh")} />
      </div>
      <div>
        <Label htmlFor="c-mensaje">{t("home.contact.message")}</Label>
        <Textarea id="c-mensaje" data-testid="contact-mensaje" value={form.mensaje} onChange={(e) => setForm({ ...form, mensaje: e.target.value })} required className="mt-1.5 min-h-[120px]" placeholder={t("home.contact.messagePh")} />
      </div>
      <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-[#0A2540] hover:bg-[#031B33] text-white h-11 font-semibold">
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} {t("home.contact.send")}
      </Button>
    </form>
  );
}

function usePrecios() {
  const { i18n } = useTranslation();
  const eq = useMonedaVisitante();
  const lng = i18n.resolvedLanguage || "es";
  const eur = (n, d = 2) => new Intl.NumberFormat(lng, { style: "currency", currency: "EUR", minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
  const local = (n) => (eq ? new Intl.NumberFormat(lng, { style: "currency", currency: eq.currency }).format(n * eq.rate) : null);
  return { eq, eur, local };
}

function PlanCard({ planKey, plan, precios, onPay, paying, oferta }) {
  const { t } = useTranslation();
  const { eq, eur, local } = precios;
  const destacado = planKey === "pro";
  const mes = num(plan.price);
  const total = num(plan.total);
  const ofertaPro = planKey === "pro" ? oferta : null;
  const badge = ofertaPro ? `Oferta de lanzamiento · ${ofertaPro.dias} días gratis` : t(`home.pricing.badges.${planKey}`, { defaultValue: "" });
  const hl = t(`home.pricing.highlights.${planKey}`, { returnObjects: true, defaultValue: [] });
  const highlights = Array.isArray(hl) ? hl : [];
  return (
    <div data-testid={`plan-card-${planKey}`}
      className={`relative rounded-2xl p-7 flex flex-col transition-all ${destacado ? "bg-gradient-to-b from-white to-cyan-50 border-2 border-[#00B4CC] shadow-xl shadow-cyan-200/60 md:-translate-y-3" : "bg-white border border-sky-100 shadow-sm"}`}>
      {badge && (
        <span data-testid={`plan-badge-${planKey}`} className={`absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1 rounded-full text-xs font-bold ${destacado ? "bg-[#00B4CC] text-white shadow-md shadow-cyan-200" : "bg-sky-100 text-[#034694] border border-sky-300"}`}>{badge}</span>
      )}
      <h3 className="font-heading font-bold text-lg mb-1 text-[#0A2540]">{t(`home.pricing.labels.${planKey}`)}</h3>
      <div className="flex items-end gap-1 mb-1">
        <span className="font-heading text-4xl font-extrabold text-[#0A2540]" data-testid={`plan-price-${planKey}`}>{t("home.pricing.perMonth", { amount: eur(mes, mes ? 2 : 0) })}</span>
      </div>
      {eq && mes > 0 && <p className="text-xs text-slate-500 mb-2" data-testid={`plan-approx-${planKey}`}>{t("home.pricing.approx", { amount: local(mes) })}</p>}
      <p className={`text-xs ${plan.ahorro_eur ? "mb-2" : "mb-5"} text-slate-500`} data-testid={`plan-periodo-${planKey}`}>{t(`home.pricing.periodo.${planKey}`, { total: eur(total) })} · {t(`home.pricing.nota.${planKey}`)}</p>
      {plan.ahorro_eur > 0 && (
        <p className="mb-5 self-start rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid={`plan-ahorro-${planKey}`}>
          {t(`home.pricing.ahorro.${planKey}`, { amount: eur(plan.ahorro_eur), pct: plan.ahorro_pct })}
        </p>
      )}
      {highlights.length > 0 && (
        <div className="space-y-2 mb-4" data-testid={`plan-highlights-${planKey}`}>
          {highlights.map((h) => (
            <div key={h} className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm font-semibold text-[#0A2540]">
              <Star className="w-4 h-4 text-amber-500 fill-amber-400 mt-0.5 shrink-0" /> {h}
            </div>
          ))}
        </div>
      )}
      <ul className="space-y-2.5 mb-7 flex-1">
        {t(`home.pricing.features.${planKey}`, { returnObjects: true }).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
            <CheckCircle2 className="w-4 h-4 text-[#0284C7] mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <LandingPlanPay planKey={planKey} total={eur(total)} destacado={destacado} onPay={onPay} loading={paying} oferta={ofertaPro} />
    </div>
  );
}

export function Pricing({ plans, onPay, paying, oferta }) {
  const { t } = useTranslation();
  const precios = usePrecios();
  return (
    <section id="precios" className="border-t border-sky-100">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-sky-100 border border-sky-300 text-[#034694] text-xs font-semibold tracking-wide uppercase mb-4">{t("home.pricing.badge")}</span>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#034694] mb-3">{t("home.pricing.title")}</h2>
          <p className="text-slate-500 max-w-2xl mx-auto">{t("home.pricing.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch" data-testid="pricing-grid">
          {PLAN_ORDER.map((key) => plans?.[key] && (
            <PlanCard key={key} planKey={key} plan={plans[key]} precios={precios} onPay={onPay} paying={paying} oferta={oferta} />
          ))}
        </div>
        {oferta && <p className="mt-8 max-w-3xl mx-auto text-center text-xs text-slate-500" data-testid="oferta-condiciones">{oferta.condiciones}</p>}
        <PayMethodBadges className="mt-8" />
        <p className="text-center text-xs text-slate-500 mt-3">{t("home.pricing.footnote")}</p>
        {precios.eq && (
          <p className="text-center text-[11px] text-slate-400 mt-1" data-testid="pricing-approx-note">
            {t("home.pricing.approxNote", { currency: precios.eq.currency })}{" "}
            <a href="https://www.exchangerate-api.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">Rates By Exchange Rate API</a>
          </p>
        )}
      </div>
    </section>
  );
}

// Sin opiniones en la portada hasta que haya clientes reales (2026-10-21): solo los sellos de confianza.
function TrustSeals() {
  const { t } = useTranslation();
  return (
    <section className="border-t border-sky-100">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="trust-badges">
          {t("home.trust.seals", { returnObjects: true }).map((s, i) => {
            const Icon = SEAL_ICONS[i] || ShieldCheck;
            return (
              <div key={s.t} className="flex items-center gap-3 bg-white border border-sky-100 rounded-xl px-5 py-4 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#0284C7]" /></div>
                <div>
                  <p className="text-sm font-semibold text-[#0A2540]">{s.t}</p>
                  <p className="text-xs text-slate-500">{s.d}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DemoAndFaq() {
  const { t } = useTranslation();
  return (
    <>
      <section id="demo" className="border-t border-sky-100">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-sky-100 border border-sky-300 text-[#034694] text-xs font-semibold tracking-wide uppercase mb-4">{t("home.demo.badge")}</span>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#034694] mb-3">{t("home.demo.title")}</h2>
          <p className="text-slate-500 max-w-2xl mx-auto mb-8">{t("home.demo.subtitle")}</p>
          <div className="relative rounded-2xl overflow-hidden border border-sky-100 shadow-xl shadow-sky-200/40 bg-[#0A2540]" data-testid="demo-video">
            {DEMO_VIDEO_URL ? (
              <div className="aspect-video">
                <video className="w-full h-full" src={DEMO_VIDEO_URL} controls playsInline preload="metadata" poster={IMG_TOOLS} data-testid="demo-video-player">
                  {t("home.demo.unsupported")}
                </video>
              </div>
            ) : (
              <div className="aspect-video relative">
                <img src={IMG_TOOLS} alt="Demo NuvliPro" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg mb-3">
                    <Play className="w-7 h-7 text-[#0A2540] ml-1" />
                  </div>
                  <p className="text-sky-100 text-sm">{t("home.demo.soon")}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="faq" className="border-t border-sky-100">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-sky-100 border border-sky-300 text-[#034694] text-xs font-semibold tracking-wide uppercase mb-4"><HelpCircle className="w-3.5 h-3.5" /> {t("home.faq.badge")}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#034694]">{t("home.faq.title")}</h2>
          </div>
          {/* Respuestas siempre en el HTML (para Google), ocultas mientras la pregunta está cerrada. */}
          <Accordion type="single" collapsible className="w-full [&_[role=region][data-state=closed]]:hidden" data-testid="faq-accordion">
            {t("home.faq.items", { returnObjects: true }).map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-sky-100">
                <AccordionTrigger className="text-[#0A2540] font-semibold text-base text-start" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent forceMount className="text-slate-600 text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}

function ContactAndFooter({ seo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <>
      <section id="contacto" className="border-t border-sky-100 bg-gradient-to-b from-white to-sky-50/60">
        <div className="max-w-5xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-100 border border-sky-300 text-[#034694] text-xs font-semibold tracking-wide uppercase mb-4">{t("home.contact.badge")}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#034694] mb-4">{t("home.contact.title")}</h2>
            <p className="text-slate-600 mb-6">{t("home.contact.subtitle")}</p>
            <div className="flex items-center gap-3 text-slate-600 text-sm">
              <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#0284C7]" /></div>
              <div>
                <p className="font-semibold text-[#0A2540]">{t("home.contact.support")}</p>
                <a href="mailto:info@nuvlipro.com" className="text-[#0284C7] font-medium hover:underline" data-testid="contact-email-link">info@nuvlipro.com</a>
                <p className="text-slate-500">{t("home.contact.supportNote")}</p>
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      <section className="border-t border-sky-100">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl font-extrabold text-[#034694] mb-4">{t("home.cta.title")}</h2>
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-slate-600 mb-8">
            {t("home.cta.items", { returnObjects: true }).map((item) => (
              <li key={item} className="flex items-center gap-1.5 text-sm"><CheckCircle2 className="w-4 h-4 text-[#0284C7]" /> {item}</li>
            ))}
          </ul>
          <Button onClick={() => navigate("/login")} className="bg-[#0A2540] hover:bg-[#031B33] text-white font-semibold h-12 px-8 text-base" data-testid="cta-create-account">
            {t("home.cta.button")}
          </Button>
        </div>
      </section>

      <CompartirWhatsApp d={seo} />
      <MapaWeb />
      <footer className="bg-[#0A2540] text-sky-100 border-t border-sky-900 px-6 py-10 text-center text-sm">
        <div className="flex items-center justify-center gap-2 mb-2">
          <img src="/nuvlipro-logo.png" alt="NuvliPro" className="w-5 h-5 rounded object-contain bg-white" />
          <span className="font-heading font-bold text-white">Nuvli<span className="text-[#38BDF8]">Pro</span></span>
        </div>
        © {new Date().getFullYear()} NuvliPro · {t("home.footer")}
        <div className="mt-2">
          <a href="mailto:info@nuvlipro.com" className="text-[#38BDF8] hover:underline" data-testid="footer-email-link">info@nuvlipro.com</a>
        </div>
        <nav className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs" data-testid="footer-legal-links">
          {LEGAL_LINKS.map(([slug, label]) => (
            <Link key={slug} to={`/legal/${slug}`} className="text-sky-200 hover:text-white hover:underline transition-colors" data-testid={`footer-legal-${slug}`}>{label}</Link>
          ))}
          <button type="button" onClick={openCookieSettings} className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 hover:underline transition-colors" data-testid="footer-cookie-settings">
            <Cookie className="w-3.5 h-3.5" /> Configurar cookies
          </button>
        </nav>
      </footer>
    </>
  );
}

// Planes y pago de la sección de precios (también la usan las páginas de aterrizaje públicas).
export function usePagoPlanes() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Al prerenderizar (y en el HTML ya generado) los planes llegan con los datos de la web pública.
  const [plans, setPlans] = useState(() => globalThis.__SEO__?.planes_detalle || null);
  const [oferta, setOferta] = useState(null);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    api.get("/subscriptions/plans").then((r) => { setPlans(r.data.plans); setOferta(r.data.oferta_lanzamiento || null); }).catch(() => {});
  }, []);

  const pagar = async (key, metodo) => {
    if (key === "gratuito") { navigate(user ? "/dashboard" : "/login?mode=register"); return; }
    const next = `/planes?plan=${key}&metodo=${metodo}&checkout=1`;
    if (!user) { navigate(`/login?mode=register&next=${encodeURIComponent(next)}`); return; }
    setPaying(`${key}:${metodo}`);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plans[key].lookup_key, origin_url: window.location.origin, metodo });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || t("home.pricing.payError"));
      setPaying(null);
    }
  };
  return { plans, oferta, paying, pagar };
}

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loginOpen = location.pathname === "/login";
  const seo = useDatosSeo();
  useCabecera(metaDe("/", seo));
  useAnaliticaPublica(seo);
  const { plans, oferta, paying, pagar } = usePagoPlanes();

  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (!ref) { return; }
    localStorage.setItem("nv_ref", ref.trim().toUpperCase().slice(0, 24));
    const t = setTimeout(() => toast.success("Te han invitado a NuvliPro: tendrás un 10 % de descuento en tu primer pago.", { id: "nv-ref" }), 300);
    return () => clearTimeout(t);
  }, [location.search]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-sky-50 to-[#E0F2FE] text-slate-800" data-testid="landing-view">
      {/* Header */}
      <header className="relative z-40 flex items-center justify-between gap-2 px-4 md:px-10 py-4 border-b border-sky-100 bg-white/80 backdrop-blur-md sticky top-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-white flex items-center justify-center shadow-sm border border-sky-100 overflow-hidden">
            <img src="/nuvlipro-logo.png" alt="NuvliPro" className="w-full h-full object-contain p-1" />
          </div>
          <span className="font-heading font-extrabold text-xl sm:text-2xl tracking-tight text-[#034694]">Nuvli<span className="text-[#0284C7]">Pro</span></span>
        </div>
        <div className="relative flex items-center gap-2 shrink-0">
          {user ? (
            <Button onClick={() => navigate("/dashboard")} className="bg-[#0A2540] hover:bg-[#031B33] text-white font-semibold px-3 sm:px-4" data-testid="landing-go-app"><span className="max-w-[7.5rem] sm:max-w-none truncate">{t("landing.irPanel")}</span> <ArrowRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <>
              <button onClick={() => navigate("/login")} aria-label={t("landing.acceder")} title={t("landing.acceder")} className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 rounded-lg text-sm font-medium text-[#034694] hover:bg-sky-50 transition-colors" data-testid="landing-login-trigger">
                <LogIn className="w-4 h-4" /> <span className="hidden sm:inline">{t("landing.acceder")}</span>
              </button>
              <Button onClick={() => navigate("/login")} className="hidden sm:inline-flex bg-[#0A2540] hover:bg-[#031B33] text-white font-semibold" data-testid="landing-register">{t("landing.pruebaGratis")}</Button>
            </>
          )}
        </div>
      </header>
      <LoginDialog open={loginOpen} onClose={() => navigate({ pathname: "/" }, { replace: true })} />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt={t("home.heroAlt")} className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/60 to-sky-100/70" />
        <div className="absolute w-[600px] h-[600px] rounded-full bg-sky-200/40 blur-3xl pointer-events-none -top-40 -left-40" />
        <HeroVideo badge={t("landing.badge")} tituloA={t("landing.heroA")} tituloB={t("landing.heroB")} />
        <div className="relative max-w-5xl mx-auto px-6 pt-8 pb-20 md:pt-10 md:pb-24 text-center">
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            {t("landing.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => navigate("/login")} className="bg-[#0A2540] hover:bg-[#031B33] text-white font-semibold h-12 px-7 text-base shadow-lg shadow-sky-950/15" data-testid="hero-cta">
              {t("landing.cta")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button onClick={() => navigate("/login")} variant="outline" className="h-12 px-7 text-base border-sky-200 bg-white text-[#034694] hover:bg-sky-50" data-testid="hero-have-account">
              {t("landing.haveAccount")}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-5">{t("landing.noCommit")}</p>
          <div className="mt-6 flex justify-center" data-testid="descarga-apps">
            <div className="w-full max-w-md rounded-2xl border border-sky-100 bg-white p-5 text-center" data-testid="worker-app-card">
              <LogoFichaje size="sm" testid="worker-app-logo" />
              <p className="text-xs text-slate-500 mt-1">{t("home.apps.workerDesc")}</p>
              <Button onClick={() => navigate("/app")} variant="outline" size="sm" className="mt-3 border-sky-200 bg-white text-[#034694] hover:bg-sky-50" data-testid="worker-app-cta">
                {t("home.apps.workerCta")} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <DescargaFichaje />

      {/* Features */}
      <section id="funcionalidades" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-sky-100 border border-sky-300 text-[#034694] text-xs font-semibold tracking-wide uppercase mb-4">{t("home.features.badge")}</span>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#034694]">{t("home.features.title")}</h2>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6" data-testid="features-grid">
          {t("home.features.items", { returnObjects: true }).map((f, i) => {
            const Icon = FEATURE_ICONS[i] || Receipt;
            return (
              <div key={f.t} className="bg-white/80 backdrop-blur-sm border border-sky-100 rounded-2xl p-6 hover:border-sky-300 hover:shadow-lg transition-all">
                <div className="w-11 h-11 rounded-xl bg-sky-100 text-[#0284C7] flex items-center justify-center mb-4"><Icon className="w-5 h-5" /></div>
                <h3 className="font-heading font-bold text-lg mb-2 text-[#0A2540]">{f.t}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.d}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-center text-sm text-slate-600" data-testid="home-presupuestos-texto">
          {t("home.features.presupuestosTexto")}{" "}
          <Link to="/programa-presupuestos-reformas" className="font-semibold text-[#0284C7] hover:underline" data-testid="home-presupuestos-link">{t("home.features.presupuestosEnlace")}</Link>
        </p>
      </section>

      {/* Image gallery */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid sm:grid-cols-3 gap-4">
          {[IMG_BUILD, IMG_KITCHEN, IMG_TOOLS].map((src) => (
            <div key={src} className="relative rounded-2xl overflow-hidden h-56 group border border-sky-100">
              <img src={src} alt={t("home.galleryAlt")} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/40 to-transparent" />
            </div>
          ))}
        </div>
      </section>

      <SeccionNuvli />
      <Pricing plans={plans} onPay={pagar} paying={paying} oferta={oferta} />
      <TrustSeals />
      <DemoAndFaq />
      <ContactAndFooter seo={seo} />
    </div>
  );
}
